import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  CompareArrows as CompareIcon,
  TipsAndUpdates as TipsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { PageHeader } from '../../components/ui';
import { useNotify } from '../../hooks';

const MARKETPLACES = [
  { value: 'wb', label: 'Wildberries' },
  { value: 'ozon', label: 'Ozon' },
  { value: 'yandex', label: 'Yandex Market' },
];

function JsonView({ value }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        bgcolor: 'grey.100',
        borderRadius: 1,
        overflowX: 'auto',
        fontSize: 12,
      }}
    >
      {JSON.stringify(value || {}, null, 2)}
    </Box>
  );
}

export default function Competitors() {
  const { success, error } = useNotify();

  const [marketplace, setMarketplace] = useState('wb');
  const [query, setQuery] = useState('');
  const [myArticle, setMyArticle] = useState('');
  const [maxResults, setMaxResults] = useState(10);

  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingCaptureId, setLoadingCaptureId] = useState(null);
  const [loadingCompareId, setLoadingCompareId] = useState(null);
  const [loadingPropose, setLoadingPropose] = useState(false);

  const [searchBlocked, setSearchBlocked] = useState(null);
  const [results, setResults] = useState([]);
  const [captureByProductId, setCaptureByProductId] = useState({});
  const [compareResult, setCompareResult] = useState(null);
  const [shotModal, setShotModal] = useState({ open: false, data: null });

  const selectedSnapshot = useMemo(() => {
    if (!compareResult?.competitor?.snapshot_id) return null;
    return Object.values(captureByProductId).find(
      (x) => Number(x?.snapshot_id) === Number(compareResult.competitor.snapshot_id)
    ) || null;
  }, [captureByProductId, compareResult]);

  const handleSearch = async () => {
    if (!query.trim()) {
      error('Введите поисковый запрос');
      return;
    }
    setLoadingSearch(true);
    setSearchBlocked(null);
    setCompareResult(null);
    try {
      const { data } = await axios.post('/ai/competitors/search', {
        marketplace,
        query: query.trim(),
        max_results: Number(maxResults) || 10,
        exclude_own_skus: true,
      });
      if (data?.blocked) {
        setResults([]);
        setSearchBlocked(data);
      } else {
        setResults(Array.isArray(data?.results) ? data.results : []);
      }
    } catch (e) {
      error(e?.response?.data?.error || e.message || 'Ошибка поиска конкурентов');
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleCapture = async (row) => {
    setLoadingCaptureId(row.product_id);
    try {
      const { data } = await axios.post('/ai/competitors/capture', {
        marketplace: row.marketplace,
        product_id: row.product_id,
        url: row.url || undefined,
      });
      if (data?.blocked) {
        error(`Снимок недоступен: ${data.reason || 'blocked'}`);
        return;
      }
      setCaptureByProductId((prev) => ({ ...prev, [row.product_id]: data }));
      success(data?.from_cache ? 'Снимок взят из кэша' : 'Снимок сохранён');
    } catch (e) {
      error(e?.response?.data?.error || e.message || 'Ошибка при создании снимка');
    } finally {
      setLoadingCaptureId(null);
    }
  };

  const handleCompare = async (row) => {
    const shot = captureByProductId[row.product_id];
    if (!shot?.snapshot_id) {
      error('Сначала сделайте снимок конкурента');
      return;
    }
    if (!myArticle.trim()) {
      error('Укажите наш артикул/ID для сравнения');
      return;
    }
    setLoadingCompareId(row.product_id);
    try {
      const { data } = await axios.post('/ai/competitors/compare', {
        my_article: myArticle.trim(),
        competitor_snapshot_id: shot.snapshot_id,
        axes: ['price', 'photos_count', 'description_length', 'rating', 'reviews_count', 'characteristics_coverage', 'content_quality'],
      });
      setCompareResult(data || null);
      success('Сравнение построено');
    } catch (e) {
      error(e?.response?.data?.error || e.message || 'Ошибка сравнения');
    } finally {
      setLoadingCompareId(null);
    }
  };

  const handlePropose = async () => {
    if (!compareResult?.competitor?.snapshot_id) {
      error('Нет данных сравнения для генерации');
      return;
    }
    setLoadingPropose(true);
    try {
      const { data } = await axios.post('/ai/competitors/propose', {
        marketplace: compareResult.competitor.marketplace,
        my_article: compareResult.my.product_id,
        competitor_snapshot_id: compareResult.competitor.snapshot_id,
        diff: compareResult.diff || {},
        recommendations_hints: compareResult.recommendations_hints || [],
      });
      success(`Черновик рекомендации создан (action_id=${data?.action_id || 'n/a'})`);
    } catch (e) {
      error(e?.response?.data?.error || e.message || 'Не удалось создать рекомендацию');
    } finally {
      setLoadingPropose(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Конкуренты"
        subtitle="Поиск конкурентов, снимок карточки и сравнение с нашей карточкой"
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Маркетплейс</InputLabel>
            <SelectLabelAware value={marketplace} onChange={setMarketplace} label="Маркетплейс">
              {MARKETPLACES.map((mp) => (
                <MenuItem key={mp.value} value={mp.value}>{mp.label}</MenuItem>
              ))}
            </SelectLabelAware>
          </FormControl>
          <TextField
            size="small"
            label="Запрос конкурентов"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Наш article/product_id"
            value={myArticle}
            onChange={(e) => setMyArticle(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            size="small"
            type="number"
            label="Результатов"
            value={maxResults}
            onChange={(e) => setMaxResults(e.target.value)}
            inputProps={{ min: 1, max: 20 }}
            sx={{ width: 120 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loadingSearch}
          >
            Найти
          </Button>
        </Stack>
      </Paper>

      {searchBlocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Поиск в degraded-режиме: `{searchBlocked.reason || 'captcha_or_antibot'}`.
        </Alert>
      )}

      <Paper sx={{ overflow: 'hidden', mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Товар</TableCell>
                <TableCell>Бренд</TableCell>
                <TableCell>Цена</TableCell>
                <TableCell>Снимок</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    Нет результатов
                  </TableCell>
                </TableRow>
              )}
              {results.map((row) => {
                const shot = captureByProductId[row.product_id];
                const hasShot = Boolean(shot?.snapshot_id);
                return (
                  <TableRow key={`${row.marketplace}-${row.product_id}`} hover>
                    <TableCell>{row.rank}</TableCell>
                    <TableCell>
                      <Tooltip title={row.url || ''}>
                        <Typography variant="body2" sx={{ maxWidth: 420 }} noWrap>
                          {row.title || row.product_id}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary">{row.product_id}</Typography>
                    </TableCell>
                    <TableCell>{row.brand || '—'}</TableCell>
                    <TableCell>{row.price_visible ? `${Number(row.price_visible).toLocaleString('ru-RU')} ₽` : '—'}</TableCell>
                    <TableCell>
                      {hasShot ? (
                        <Chip
                          size="small"
                          label={shot.from_cache ? 'Кэш' : 'Снято'}
                          color={shot.vision_degraded ? 'warning' : 'success'}
                          variant="outlined"
                          onClick={() => setShotModal({ open: true, data: shot })}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CameraIcon />}
                          onClick={() => handleCapture(row)}
                          disabled={loadingCaptureId === row.product_id}
                        >
                          Снимок
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CompareIcon />}
                          onClick={() => handleCompare(row)}
                          disabled={!hasShot || loadingCompareId === row.product_id}
                        >
                          Сравнить
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {compareResult && (
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>Наша карточка</Typography>
              <Typography variant="body2">{compareResult.my?.title || compareResult.my?.product_id}</Typography>
              <Typography variant="caption" color="text.secondary">
                Фото: {compareResult.my?.photos_count ?? '—'} | Описание: {compareResult.my?.description_length ?? '—'} симв.
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>Конкурент</Typography>
              <Typography variant="body2">{compareResult.competitor?.title || compareResult.competitor?.product_id}</Typography>
              <Typography variant="caption" color="text.secondary">
                Фото: {compareResult.competitor?.photos_count ?? '—'} | Описание: {compareResult.competitor?.description_length ?? '—'} симв.
              </Typography>
            </Box>
          </Stack>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Diff</Typography>
          <JsonView value={compareResult.diff} />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Подсказки</Typography>
          <Stack spacing={1}>
            {(compareResult.recommendations_hints || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">Подсказки не сформированы</Typography>
            )}
            {(compareResult.recommendations_hints || []).map((hint, idx) => (
              <Alert key={idx} severity="info">{hint}</Alert>
            ))}
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<TipsIcon />}
              onClick={handlePropose}
              disabled={loadingPropose}
            >
              Сгенерировать рекомендации
            </Button>
            {selectedSnapshot?.screenshot_url && (
              <Button
                variant="outlined"
                onClick={() => setShotModal({ open: true, data: selectedSnapshot })}
              >
                Открыть снимок
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      <Dialog open={shotModal.open} onClose={() => setShotModal({ open: false, data: null })} maxWidth="lg" fullWidth>
        <DialogTitle>Снимок конкурента</DialogTitle>
        <DialogContent dividers>
          {shotModal.data?.screenshot_url ? (
            <Box
              component="img"
              src={`${shotModal.data.screenshot_url}?t=${encodeURIComponent(localStorage.getItem('token') || '')}`}
              alt="competitor-shot"
              sx={{ width: '100%', maxHeight: 480, objectFit: 'contain', mb: 2, bgcolor: 'grey.100', borderRadius: 1 }}
            />
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>Скриншот недоступен (возможно TTL-очистка).</Alert>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Extracted JSON</Typography>
          <JsonView value={shotModal.data?.extracted} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function SelectLabelAware({ value, onChange, label, children }) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {children}
    </TextField>
  );
}
